import {
  CustomFieldType,
  type CustomFieldDefinition,
  type ResourceTypeModel
} from '__types__';
import RESOURCE_CONFIG from 'v1/helpers/resourceTypeHelper';
import CONTACT_INFO_TYPES from 'v1/helpers/DropdownDataSets/CONTACT_INFO_TYPES.json';
import WEBSITE_INFO_TYPES from 'v1/helpers/DropdownDataSets/WEBSITE_INFO_TYPES.json';
import SOCIAL_PROVIDERS from 'v1/helpers/DropdownDataSets/SOCIAL_PROVIDERS.json';
import AREAS from 'v1/helpers/measurements/area';
import LENGTHS from 'v1/helpers/measurements/length';
import MASSES from 'v1/helpers/measurements/mass';
import {
  MEASUREMENT_UNITS,
  type ContactDetailColumn,
  type ContactDetailType,
  type FieldSpec,
  type GroupOption,
  type MeasurementUnit,
  type ResourceScalarColumn,
  type RateCardOption
} from 'v5/core/entity-schema/entitySchema.types';
import { type SelectOption } from 'v5/core/form-schema/formSchema.types';
import type { ResourceSchemaCopy } from './resourceSchemaCopy';
import type { ResolvedMetastructureFieldCore } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';

/**
 * Static inputs a resource `FieldSpec` needs beyond the metastructure field
 * itself (ported from v5 `FormGenerationMetadata`).
 */
export type SpecOptions = {
  resourceTypeModel: ResourceTypeModel;
  resourceTypeId: number;
  tags: SelectOption[];
  allowCreateTags: boolean;
  groups: GroupOption[];
  pronouns: Array<{ id: number; value: string }>;
  /** Rate currencies: `value` is the ISO code, `label` the currency name ("Canadian Dollar"). */
  currencies: SelectOption[];
  /** Workspace default currency (`defaults_currency` setting), used for new rates. */
  defaultCurrency: string;
  /** Rate cards a contact rate may link to. */
  rateCards: RateCardOption[];
  /** Rate cards settings page; `null` without `CONFIGURE_RATE_CARDS`. */
  rateCardsSettingsPath: string | null;
  copy: ResourceSchemaCopy;
};

const CORE_FIELD_KEY_PREFIX = 'core';
export const CUSTOM_FIELD_KEY_PREFIX = 'custom';

export function coreFieldKey(name: string): string {
  return `${CORE_FIELD_KEY_PREFIX}:${name}`;
}

function customFieldKey(
  definitionId: number,
  keyPrefix: string = CUSTOM_FIELD_KEY_PREFIX
): string {
  return `${keyPrefix}:${definitionId}`;
}

/** `{ value, label }` copies of the legacy dropdown datasets (`v1/helpers/DropdownDataSets`). */
function toSelectOptions(
  entries: ReadonlyArray<{ value: string; label: string }>
): SelectOption[] {
  return entries.map(entry => ({ value: entry.value, label: entry.label }));
}

/** Legacy `CONTACT_INFO_TYPES.json` (work / personal / agent / guardian / other) for emails and phones. */
export const CONTACT_INFO_TYPE_OPTIONS: SelectOption[] =
  toSelectOptions(CONTACT_INFO_TYPES);
/** Legacy `WEBSITE_INFO_TYPES.json`: the contact types without guardian, as the v1 form offers them. */
export const WEBSITE_INFO_TYPE_OPTIONS: SelectOption[] =
  toSelectOptions(WEBSITE_INFO_TYPES);
/** Legacy `SOCIAL_PROVIDERS.json`; values are the `socialResolvers` platform keys. */
export const SOCIAL_PROVIDER_OPTIONS: SelectOption[] =
  toSelectOptions(SOCIAL_PROVIDERS);

const CONTACT_DETAIL_CONFIG: Record<
  ContactDetailColumn,
  { detailType: ContactDetailType; typeOptions: SelectOption[] }
> = {
  emails: {
    detailType: 'EMAIL',
    typeOptions: CONTACT_INFO_TYPE_OPTIONS
  },
  phone_numbers: {
    detailType: 'PHONE',
    typeOptions: CONTACT_INFO_TYPE_OPTIONS
  },
  socials: {
    detailType: 'SOCIAL',
    typeOptions: SOCIAL_PROVIDER_OPTIONS
  },
  websites: {
    detailType: 'WEBSITE',
    typeOptions: WEBSITE_INFO_TYPE_OPTIONS
  }
};

const SCALAR_COLUMNS: ReadonlyArray<ResourceScalarColumn> = [
  'nick_name',
  'role'
];

function isScalarColumn(name: string): name is ResourceScalarColumn {
  return (SCALAR_COLUMNS as ReadonlyArray<string>).includes(name);
}

function isContactDetailName(
  name: string
): name is keyof typeof CONTACT_DETAIL_CONFIG {
  return name in CONTACT_DETAIL_CONFIG;
}

/** `RESOURCE_CONFIG[model].resourcePageLayout.showSingleNameField` (v5 `name.control`). */
export function usesSingleNameField(model: ResourceTypeModel): boolean {
  return !!RESOURCE_CONFIG[model]?.resourcePageLayout.showSingleNameField;
}

function coreBase(
  field: ResolvedMetastructureFieldCore,
  label: string
): {
  key: string;
  label: string;
  description: null;
  required: boolean;
  locked: boolean;
} {
  return {
    key: coreFieldKey(field.name),
    label,
    description: null,
    required: field.required,
    locked: field.locked
  };
}

/**
 * Port of v5 `crateCoreControl`. Returns `null` for core names that have no
 * field kind yet; the resolver skips those silently.
 */
export function coreFieldSpec(
  field: ResolvedMetastructureFieldCore,
  options: SpecOptions
): FieldSpec | null {
  const name = field.name;

  if (name === 'full_name') {
    return {
      ...coreBase(field, options.copy.coreLabel.full_name),
      kind: 'name',
      layout: usesSingleNameField(options.resourceTypeModel)
        ? 'single'
        : 'first-last'
    };
  }
  if (name === 'pronouns') {
    return {
      ...coreBase(field, options.copy.coreLabel.pronouns),
      kind: 'pronouns',
      options: options.pronouns
    };
  }
  if (isContactDetailName(name)) {
    const config = CONTACT_DETAIL_CONFIG[name];
    return {
      ...coreBase(field, options.copy.coreLabel[name]),
      kind: 'contact-details',
      detailType: config.detailType,
      typeOptions: config.typeOptions,
      platformRecognition: config.detailType === 'SOCIAL'
    };
  }
  if (name === 'tags') {
    return {
      ...coreBase(field, options.copy.coreLabel.tags),
      kind: 'tags',
      options: options.tags,
      allowCreate: options.allowCreateTags
    };
  }
  if (name === 'groups') {
    return {
      ...coreBase(field, options.copy.coreLabel.groups),
      kind: 'groups',
      options: options.groups,
      resourceTypeId: options.resourceTypeId
    };
  }
  if (name === 'locations') {
    return {
      ...coreBase(field, options.copy.coreLabel.locations),
      kind: 'location'
    };
  }
  if (name === 'rates') {
    return {
      ...coreBase(field, options.copy.coreLabel.rates),
      kind: 'rate',
      currencies: options.currencies,
      defaultCurrency: options.defaultCurrency,
      rateCards: options.rateCards,
      rateCardsSettingsPath: options.rateCardsSettingsPath
    };
  }
  if (name === 'represented_by_id') {
    return {
      ...coreBase(field, options.copy.coreLabel.represented_by_id),
      kind: 'entity-ref',
      entity: 'resource',
      column: 'represented_by_id',
      resourceTypeModels: ['AGENT']
    };
  }
  if (name === 'organisation_id') {
    return {
      ...coreBase(field, options.copy.coreLabel.organisation_id),
      kind: 'entity-ref',
      entity: 'resource',
      column: 'organisation_id',
      resourceTypeModels: ['ORGANIZATION']
    };
  }
  if (isScalarColumn(name)) {
    const label = options.copy.coreLabel[name];
    return {
      ...coreBase(field, label),
      kind: 'text',
      multiline: false,
      placeholder: options.copy.enterPlaceholder(label),
      source: { type: 'core', column: name }
    };
  }
  return null;
}

/** v5 write path (`MutationService.impl`) falls back to feet when the definition has no default. */
const DEFAULT_MEASUREMENT_UNIT: MeasurementUnit = 'ft';

function toMeasurementUnit(unit: string | null): MeasurementUnit {
  return (MEASUREMENT_UNITS as ReadonlyArray<string>).includes(unit ?? '')
    ? (unit as MeasurementUnit)
    : DEFAULT_MEASUREMENT_UNIT;
}

function definitionOptions(definition: CustomFieldDefinition): SelectOption[] {
  return (definition.options ?? []).map(option => ({
    value: String(option.id),
    label: option.value ?? String(option.id)
  }));
}

/** Legacy `Dimension2DInput` / `Dimension3DInput` default: the length units. */
export const DIMENSION_UNIT_OPTIONS: SelectOption[] = toSelectOptions(LENGTHS);
/** Legacy `MeasurementInput` default: length + area + mass units. */
export const MEASUREMENT_LEGACY_UNIT_OPTIONS: SelectOption[] = toSelectOptions([
  ...LENGTHS,
  ...AREAS,
  ...MASSES
]);

/**
 * Port of v5 `createCustomFieldControl` and the legacy
 * `FormBuilderCustomFields` map, keyed by `CustomFieldType`. NetSuite
 * subclass / PO / vendor render as plain select / text, as the legacy map
 * does; unknown types fall back to single-line text.
 *
 * @param required already combined by the caller (`definition.required || field.required`)
 * @param locked from the metastructure field / binding assignment
 * @param keyPrefix key = `<keyPrefix>:<definition.id>` (`custom` by default)
 */
export function customFieldSpec(
  definition: CustomFieldDefinition,
  required: boolean,
  locked: boolean,
  copy: ResourceSchemaCopy,
  keyPrefix: string = CUSTOM_FIELD_KEY_PREFIX
): FieldSpec {
  const base = {
    key: customFieldKey(definition.id, keyPrefix),
    label: definition.name,
    description: null,
    required,
    locked
  };

  switch (definition.data_type) {
    case CustomFieldType.LONG_TEXT:
      return {
        ...base,
        kind: 'text',
        multiline: true,
        placeholder: copy.enterPlaceholder(definition.name),
        source: { type: 'custom', definition }
      };
    case CustomFieldType.BOOLEAN:
      return { ...base, kind: 'boolean', definition };
    case CustomFieldType.DATE:
      return { ...base, kind: 'date', definition };
    case CustomFieldType.DATE_RANGE:
      return { ...base, kind: 'date-range', definition };
    case CustomFieldType.SINGLE_SELECT:
    case CustomFieldType.NETSUITE_SUBCLASS:
      return {
        ...base,
        kind: 'single-select',
        options: definitionOptions(definition),
        clearable: !required,
        definition
      };
    case CustomFieldType.MULTI_SELECT:
      return {
        ...base,
        kind: 'multi-select',
        options: definitionOptions(definition),
        definition
      };
    case CustomFieldType.NUMBER:
      return { ...base, kind: 'number', definition };
    case CustomFieldType.MEASUREMENT_NEW:
      return {
        ...base,
        kind: 'measurement',
        unit: toMeasurementUnit(definition.measurement_default_unit),
        unitLocked: definition.measurement_unit_lock ?? false,
        definition
      };
    case CustomFieldType.CURRENCY:
      return { ...base, kind: 'currency', definition };
    case CustomFieldType.URL:
      return { ...base, kind: 'url', placeholder: 'Enter link', definition };
    case CustomFieldType.PERCENTAGE:
      return { ...base, kind: 'percentage', definition };
    case CustomFieldType.HEIGHT:
      return { ...base, kind: 'height', definition };
    case CustomFieldType.DIMENSION_2D:
      return {
        ...base,
        kind: 'dimension',
        axes: 2,
        units: DIMENSION_UNIT_OPTIONS,
        definition
      };
    case CustomFieldType.DIMENSION_3D:
      return {
        ...base,
        kind: 'dimension',
        axes: 3,
        units: DIMENSION_UNIT_OPTIONS,
        definition
      };
    case CustomFieldType.MEASUREMENT:
      return {
        ...base,
        kind: 'measurement-legacy',
        units: MEASUREMENT_LEGACY_UNIT_OPTIONS,
        definition
      };
    case CustomFieldType.NETSUITE_MASTER_PROJECT:
      return { ...base, kind: 'netsuite-master-project', definition };
    case CustomFieldType.SHORT_TEXT:
    case CustomFieldType.NETSUITE_PO:
    case CustomFieldType.NETSUITE_VENDOR:
    default:
      return {
        ...base,
        kind: 'text',
        multiline: false,
        placeholder: copy.enterPlaceholder(definition.name),
        source: { type: 'custom', definition }
      };
  }
}
